import {
  List,
  ListItemButton,
  Radio,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  type Provider,
  type Account,
  type FileSnapshot,
  type Snapshot,
  AccountState,
} from '@repo/api-gw-sdk';
import { type Dispatch, type SetStateAction } from 'react';

import { AccountIndicator } from '@/components/accounts/accountIndicator';
import { Loader } from '@/components/layout/loading';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { isAccountFeatureSupported } from '@/utils/roles';

import { StepContainer } from '../../wizard/StepContainer';

interface RestoreAccountProps {
  supportedCloudProviders: Provider[];
  restoreAccount?: Account;
  snapshot?: Snapshot;
  fileSnapshot?: FileSnapshot;
  vaultId?: string;
  supportedInstalledVersion?: string;
}

type SelectTargetAccountStepProps<T> = StepProps<T & RestoreAccountProps>;

export const SelectTargetAccountStep = <T,>(
  props: SelectTargetAccountStepProps<T>
) => {
  return (
    <StepContainer
      stepperLabels={props.stepperLabels}
      stepperIndex={0}
      onBackClick={props.back}
      canGoNext={() => !!props.wizardState.restoreAccount}
      onNextClick={async () => props.setNextStep(props.currentStep.next?.[0])}
      toolbarComponent={
        props.wizardState.snapshot ? (
          <SnapshotToolbar
            pointInTime={props.wizardState.snapshot.pointInTime}
            vaultId={props.wizardState.snapshot.vaultId}
            accessDeniedItemsCount={
              props.wizardState.snapshot.accessDeniedItemsCount
            }
            supportPiTR={props.wizardState.snapshot.supportPiTR}
          />
        ) : (
          props.wizardState.fileSnapshot &&
          props.wizardState.vaultId && (
            <SnapshotToolbar
              pointInTime={props.wizardState.fileSnapshot.generatedOn}
              vaultId={props.wizardState.vaultId}
              supportPiTR={false}
            />
          )
        )
      }
    >
      <SelectTargetAccount
        {...props.wizardState}
        setRestoreAccountState={props.setWizardState}
      />
    </StepContainer>
  );
};

interface SelectTargetAccountProps<T> extends RestoreAccountProps {
  setRestoreAccountState: Dispatch<
    SetStateAction<
      T & {
        restoreAccount?: Account;
      }
    >
  >;
  title?: string;
}

export const SelectTargetAccount = <T,>(props: SelectTargetAccountProps<T>) => {
  const dal = useDAL();
  const { data: body } = dal.cloudAccounts.restore.list();

  if (!body) {
    return <Loader />;
  }

  const onRestoreAccountClicked = (account: Account) => {
    props.setRestoreAccountState((state) => ({
      ...state,
      restoreAccount: account,
    }));
  };

  return (
    <>
      <Typography component={'span'}>
        {props.title || 'Select an account to restore to:'}
      </Typography>
      <List className='mt-[20px] mb-[10px]'>
        {body.accounts
          .filter(
            (account) =>
              account.state === AccountState.ACCOUNT_STATE_CONNECTED &&
              (!props.supportedCloudProviders ||
                props.supportedCloudProviders.includes(account.cloudProvider))
          )
          .map((account) => {
            const isChecked = props.restoreAccount?.id === account.id;
            const isDisabled = Boolean(
              props.supportedInstalledVersion &&
                !isAccountFeatureSupported(
                  props.supportedInstalledVersion,
                  account
                )
            );
            return (
              <Tooltip
                key={account.id}
                title={
                  isDisabled
                    ? "This account doesn't have the required permissions. Please update the account's role"
                    : undefined
                }
              >
                <span>
                  <ListItemButton
                    key={account.id}
                    sx={{
                      border: isChecked
                        ? '2px solid var(--mui-palette-primary-main)'
                        : '1px solid var(--mui-palette-divider)',
                      padding: isChecked ? '16px 24px' : '17px 25px',
                      marginBottom: '8px',
                    }}
                    onClick={() => onRestoreAccountClicked(account)}
                    className='flex'
                    disabled={isDisabled}
                  >
                    <Radio
                      className='p-0'
                      checked={isChecked}
                      value={account.id}
                    />
                    <AccountIndicator account={account} />
                  </ListItemButton>
                </span>
              </Tooltip>
            );
          })}
      </List>
    </>
  );
};
